import { Checklist, ChecklistProcedure } from '@/domain/models/Checklist'

import { toSafeISOString } from '@/utils/safe'

export interface ChecklistWirePojo {
  id: string
  userAircraftId: string
  name: string
  procedures: string
  createdAt: string
  updatedAt: string
  // Read-only: the API answers with the soft-delete stamp, but the client never writes it.
  deletedAt?: string | null
}

export interface ChecklistListResponse {
  checklists: ChecklistWirePojo[]
}

export interface ChecklistDetailResponse {
  checklist: ChecklistWirePojo
}

/**
 * Hard limits of the `user_checklist` table. The backend integration guide asks the client to check
 * them: past any of these the API answers `500`, with no usable message.
 */
export const CHECKLIST_PROCEDURES_MAX_BYTES = 65535 // MySQL TEXT column
export const CHECKLIST_NAME_MAX_CHARS = 120
export const CHECKLIST_ID_MAX_CHARS = 36

export const CHECKLIST_TOO_LARGE = 'CHECKLIST_TOO_LARGE'
export const CHECKLIST_NAME_TOO_LONG = 'CHECKLIST_NAME_TOO_LONG'
export const CHECKLIST_ID_TOO_LONG = 'CHECKLIST_ID_TOO_LONG'

/**
 * Procedure and item ids never travel on the wire: they are local to the client that produced them.
 * The server round-trips `procedures` as an opaque JSON string, and `parseChecklistProcedures`
 * regenerates positional ids on the way back in. Kept byte-for-byte compatible with the pilotapp
 * (`src/services/userChecklistsSync/ChecklistsSyncService.ts`) so a checklist written by either app
 * is indistinguishable to the other.
 */
export const serializeProceduresForWire = (procedures: ChecklistProcedure[]): string => {
  const compactProcedures = (procedures ?? []).map((procedure) => ({
    name: procedure.name,
    type: procedure.type,
    items: (procedure.items ?? []).map((item) => {
      if (item.type === 'divider') {
        return {
          type: 'divider' as const,
          title: item.title
        }
      }

      if (item.type === 'check') {
        return {
          type: 'check' as const,
          title: item.title,
          action: item.action,
          ...(item.description ? { description: item.description } : {})
        }
      }

      return {
        type: 'note' as const,
        title: item.title,
        description: item.description
      }
    })
  }))

  return JSON.stringify(compactProcedures)
}

export const toChecklistWirePojo = (checklist: Checklist): ChecklistWirePojo => {
  const pojo = checklist.toPojo()

  return {
    id: pojo.id,
    userAircraftId: pojo.userAircraftId,
    name: pojo.name,
    procedures: serializeProceduresForWire(checklist.procedures),
    createdAt: toSafeISOString(pojo.createdAt),
    updatedAt: toSafeISOString(pojo.updatedAt)
  }
}

/**
 * Returns the error key of the first limit the payload breaks, or `null` when it fits.
 *
 * `procedures` is measured in bytes, not characters: the column caps bytes and an accented character
 * costs two. The biggest template of the catalogue (`AFM 7.01.16-E, Rev 7`, 71.644 bytes) already
 * does not fit, so this is a real user-facing case, not a defensive check.
 */
export const findChecklistWireLimitViolation = (pojo: ChecklistWirePojo): string | null => {
  if (new TextEncoder().encode(pojo?.procedures ?? '').length > CHECKLIST_PROCEDURES_MAX_BYTES) {
    return CHECKLIST_TOO_LARGE
  }

  if ((pojo?.name ?? '').length > CHECKLIST_NAME_MAX_CHARS) return CHECKLIST_NAME_TOO_LONG

  if ((pojo?.id ?? '').length > CHECKLIST_ID_MAX_CHARS) return CHECKLIST_ID_TOO_LONG

  return null
}
